import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, ChevronRight, ExternalLink, X } from "lucide-react";
import { useLang, useT } from "@/i18n/LanguageContext";
import { type Source, type SourceKind } from "@/data/sources";
import { fallbackPageSources, pageSourcesQuery } from "@/lib/public-sources.functions";


const KIND_STYLE: Record<SourceKind, { bg: string; border: string; fg: string }> = {
  leitlinie: { bg: "rgba(34,211,238,0.12)", border: "rgba(34,211,238,0.45)", fg: "#67e8f9" },
  behoerde: { bg: "rgba(168,85,247,0.14)", border: "rgba(168,85,247,0.45)", fg: "#d8b4fe" },
  fachinfo: { bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.45)", fg: "#86efac" },
  monitoring: { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.45)", fg: "#fcd34d" },
  sekundaerliteratur: { bg: "rgba(156,163,175,0.14)", border: "rgba(156,163,175,0.45)", fg: "#e5e7eb" },
  primaerliteratur: { bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.45)", fg: "#93c5fd" },
};

const KIND_KEY: Record<
  SourceKind,
  | "sources.kind.leitlinie"
  | "sources.kind.behoerde"
  | "sources.kind.fachinfo"
  | "sources.kind.monitoring"
  | "sources.kind.sekundaerliteratur"
  | "sources.kind.primaerliteratur"
> = {
  leitlinie: "sources.kind.leitlinie",
  behoerde: "sources.kind.behoerde",
  fachinfo: "sources.kind.fachinfo",
  monitoring: "sources.kind.monitoring",
  sekundaerliteratur: "sources.kind.sekundaerliteratur",
  primaerliteratur: "sources.kind.primaerliteratur",
};

function SourceCard({ s }: { s: Source }) {
  const t = useT();
  const { lang } = useLang();
  const style = KIND_STYLE[s.kind];
  const backs = lang === "de" ? s.backs_de : s.backs_en;
  const caveat = lang === "de" ? s.caveat_de : s.caveat_en;
  return (
    <li
      style={{
        listStyle: "none",
        padding: "0.85rem 0.95rem",
        borderRadius: 10,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.10)",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      <span
        style={{
          alignSelf: "flex-start",
          padding: "2px 9px",
          borderRadius: 999,
          background: style.bg,
          border: `1px solid ${style.border}`,
          color: style.fg,
          fontSize: "0.7rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {t(KIND_KEY[s.kind])}
      </span>

      <a
        href={s.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${s.title} (${t("sources.newTab")})`}
        style={{
          color: "var(--accent-cyan, #67e8f9)",
          fontSize: "0.9rem",
          fontWeight: 600,
          lineHeight: 1.45,
          textDecoration: "underline",
          display: "inline-flex",
          alignItems: "flex-start",
          gap: "0.35rem",
        }}
      >
        <span>{s.title}</span>
        <ExternalLink size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }} />
      </a>

      <div style={{ color: "var(--text-muted)", fontSize: "0.78rem", lineHeight: 1.5 }}>
        {s.publisher} · {s.year}
      </div>

      <p style={{ margin: 0, color: "var(--text-primary)", fontSize: "0.83rem", lineHeight: 1.55 }}>
        <strong style={{ color: "var(--text-secondary)" }}>{t("sources.backs")}</strong> {backs}
      </p>

      {caveat && (
        <p
          style={{
            margin: 0,
            padding: "0.6rem 0.75rem",
            borderRadius: 8,
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.35)",
            color: "var(--text-primary)",
            fontSize: "0.8rem",
            lineHeight: 1.55,
          }}
        >
          <strong style={{ color: "#fcd34d" }}>{t("sources.caveat")}</strong> {caveat}
        </p>
      )}
    </li>
  );
}

export function PageSources({
  path,
  className,
  style,
}: {
  path: string;
  className?: string;
  style?: CSSProperties;
}) {
  const t = useT();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  const fallback = fallbackPageSources(path);
  // placeholderData (not initialData): the fallback is shown immediately but the
  // query still fetches, so edited page sources from the database win.
  const { data: loaded } = useQuery({
    ...pageSourcesQuery(path),
    placeholderData: fallback ?? undefined,
  });
  const entry = loaded ?? fallback;


  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(rm.matches);
    update();
    rm.addEventListener("change", update);
    return () => rm.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
      if (e.key === "Tab" && dialogRef.current) {
        const nodes = dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && (active === first || active === dialogRef.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      const id = window.setTimeout(() => closeRef.current?.focus(), 30);
      return () => window.clearTimeout(id);
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!entry) return null;

  const intro = lang === "de" ? entry.intro_de : entry.intro_en;
  const openClaims = lang === "de" ? entry.openClaims_de : entry.openClaims_en;
  const sources: Source[] = entry.sources;
  const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
  const dur = reducedMotion ? "0ms" : "300ms";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={["glass-card", className].filter(Boolean).join(" ")}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          marginTop: "1.5rem",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "0.7rem",
          padding: "0.9rem 1.1rem",
          minHeight: 48,
          borderRadius: 10,
          cursor: "pointer",
          color: "var(--text-primary)",
          fontSize: "0.92rem",
          fontWeight: 600,
          textAlign: "left",
          ...style,
        }}
      >
        <BookOpen size={18} aria-hidden="true" style={{ color: "var(--accent-cyan, #67e8f9)", flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{t("sources.button")}</span>
        <ChevronRight size={18} aria-hidden="true" style={{ color: "var(--text-muted)", flexShrink: 0 }} />
      </button>

      <div
        onClick={close}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: `opacity ${dur} ${ease}`,
          zIndex: 60,
        }}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-hidden={!open}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100dvh",
          width: "min(92vw, 460px)",
          transform: open ? "translate3d(0,0,0)" : "translate3d(100%,0,0)",
          transition: `transform ${dur} ${ease}`,
          background: "var(--bg-surface, #0d0d12)",
          color: "var(--text-primary)",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          boxShadow: open ? "-16px 0 40px rgba(0,0,0,0.45)" : "none",
          padding: "1.1rem 1.25rem calc(1.5rem + env(safe-area-inset-bottom)) 1.25rem",
          overflowY: "auto",
          overscrollBehavior: "contain",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          zIndex: 61,
          visibility: open ? "visible" : "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "var(--text-primary)" }}>
            {t("sources.title")}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            aria-label={t("sources.close")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <p style={{ margin: 0, fontSize: "0.86rem", lineHeight: 1.6, color: "var(--text-secondary)" }}>{intro}</p>

        <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          <h3
            style={{
              margin: 0,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--text-muted)",
              fontWeight: 700,
            }}
          >
            {t("sources.section.verified")}
          </h3>
          <ul style={{ margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {sources.map((s) => (
              <SourceCard key={s.id} s={s} />
            ))}
          </ul>
        </section>

        <section style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          <h3
            style={{
              margin: 0,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--text-muted)",
              fontWeight: 700,
            }}
          >
            {t("sources.section.open")}
          </h3>
          <div
            style={{
              padding: "0.85rem 0.95rem",
              borderRadius: 10,
              background: "rgba(220,38,38,0.08)",
              border: "1px solid rgba(220,38,38,0.4)",
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#fca5a5", fontSize: "0.83rem", fontWeight: 700 }}>
              <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
              <span>{t("sources.section.openIntro")}</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: "1.1rem", display: "flex", flexDirection: "column", gap: "0.45rem" }}>
              {openClaims.map((c, i) => (
                <li key={i} style={{ fontSize: "0.82rem", lineHeight: 1.55, color: "var(--text-primary)" }}>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <p
          style={{
            margin: 0,
            paddingTop: "0.75rem",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            fontSize: "0.8rem",
            lineHeight: 1.6,
            color: "var(--text-secondary)",
          }}
        >
          {t("sources.disclaimer")}{" "}
          <a href="mailto:Contact@RestLicht.FYI" style={{ color: "var(--accent-cyan, #67e8f9)", textDecoration: "underline" }}>
            Contact@RestLicht.FYI
          </a>
        </p>
      </div>
    </>
  );
}
